import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { IconButton, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LinkIcon from '@mui/icons-material/Link'
import IosShareIcon from '@mui/icons-material/IosShare'
import PublicIcon from '@mui/icons-material/Public'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import BulkShareFilesModal from 'features/bpDetails/Components/ShareBulkFilesModal'
import { toast } from 'react-toastify'
import MarkdownRenderer from 'components/MarkdownRenderer'
import LogoShort from 'assets/images/bp-logo-orange.png'
import InitialsAvatar from 'features/navigation/components/InitialsAvatar'
import GuruActionCard from './GuruActionCard'
import GuruPhotoSources from './GuruPhotoSources'
import ReportSourceCitation from '../reporting/ReportSourceCitation'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { recordGuruFeedback } from 'services/GuruChatService'
import { fetchBpRoutine } from 'features/bpDetails/ducks/actions'
import APIService from 'services/APIService'

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (e) { /* fall through */ }
  // Fallback for older browsers / insecure contexts.
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    return true
  } catch (e) {
    return false
  }
}

const TOOL_LABELS = {
  get_building_summary: 'Checked building overview',
  get_units: 'Looked up units',
  get_tasks: 'Looked up tasks',
  get_members: 'Looked up members',
  get_files_metadata: 'Searched files',
  get_forms: 'Looked up form submissions',
  get_approvals: 'Looked up approvals',
  get_notes: 'Searched building notes',
  get_emails: 'Searched inbox emails',
  get_statistics: 'Checked building activity & stats',
  get_checklists: 'Looked up compliance checklists',
  get_compliance_programme: 'Checked the compliance programme',
  get_estates: 'Looked up estates',
  get_actions: 'Looked up open remedials',
  get_incidents: 'Looked up incidents',
  search_documents: 'Searched document contents',
  create_task: 'Created a task',
  update_task: 'Updated a task',
  assign_member: 'Assigned a member',
  create_form_response: 'Submitted a form',
  request_approval: 'Requested an approval',
  create_incident: 'Logged an incident',
  convert_action_to_task: 'Created a task from a remedial',
  get_document_task_links: 'Checked which tasks and documents are connected',
  link_remedial_to_task: 'Linked a remedial to a task',
}

const MessageContainer = styled.div`
  display: flex;
  gap: 16px;
  max-width: 100%;
  margin-bottom: 8px;
  justify-content: ${({ $isUser }) => $isUser ? 'flex-end' : 'flex-start'};

  @media (max-width: 768px) {
    gap: 8px;
  }
`

const MessageContent = styled.div`
  background-color: ${({ $isUser, $isError }) =>
    $isError
      ? '#ffebee'
      : $isUser
        ? '#f57c0015'
        : '#f7f7f8'};
  border-radius: 8px;
  padding: 12px 16px;
  max-width: 70%;
  min-width: 0;
  color: ${({ $isError, theme }) => $isError ? '#c62828' : theme.colors.haiti};
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 10px 12px;
    font-size: 14px;
  }
`

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
`

const MessageSender = styled.div`
  font-weight: 600;
  color: ${({ $isUser, theme }) =>
    $isUser ? theme.colors.haiti : theme.colors.primary};

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const MessageTime = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  text-align: right;
  margin-top: 8px;
`

const HeaderAvatar = styled.img`
  width: 32px !important;
  height: 32px !important;
  min-width: 32px;
  min-height: 32px;
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px;
    min-height: 28px;
    max-width: 28px;
    max-height: 28px;
  }
`

const UserAvatar = styled.img`
  width: 32px !important;
  height: 32px !important;
  min-width: 32px;
  min-height: 32px;
  max-width: 32px;
  max-height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px;
    min-height: 28px;
    max-width: 28px;
    max-height: 28px;
  }
`

const AvatarWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;

  @media (max-width: 768px) {
    display: none;
  }
`

const ToolInfoIcon = styled(IconButton)`
  padding: 2px !important;
  color: ${({ theme }) => theme.colors.grey[200]} !important;

  svg {
    font-size: 16px;
  }
`

const ToolList = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  padding: 4px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const ToolChip = styled.span`
  background: ${({ theme }) => theme.colors.lightGrey || '#eee'};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
`

const CitationsContainer = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e8e8e8;
`

const CitationsLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CitationsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const AiDisclaimer = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-top: 8px;
  font-style: italic;
  line-height: 1.4;
`

const WebSourcesContainer = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  background: #f0f7ff;
  border-radius: 6px;
  border: 1px solid #d0e3f7;
`

const WebSourcesLabel = styled.div`
  font-size: 11px;
  color: #33becc;
  font-weight: 600;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const WebSourcesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const WebSourceChip = styled.span`
  font-size: 12px;
  color: #33becc;
  background: #e6f7f9;
  padding: 2px 8px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #bbdefb;
  }
`

const WebSourcesDisclaimer = styled.div`
  font-size: 10px;
  color: #666;
  margin-top: 6px;
  font-style: italic;
`

// Per-citation row: the existing source pill, plus a small "copy link" icon
// so users can share a deeplink to a single source with other BP members.
// inline-flex preserves the original CitationsList wrap-and-flow layout
// (chips sit side by side, wrap to next line on overflow) — pair stays glued.
const CitationRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`

// Bare wrapper so the existing ReportSourceCitation styling stays intact.
const CitationSlot = styled.div`
  display: inline-flex;
`

const ShareSourceBtn = styled(IconButton)`
  && {
    padding: 4px;
    color: #64748b;
    &:hover {
      background: #e2e8f0;
      color: #0f172a;
    }
  }
`

// "Share Q&A" footer on assistant bubbles. Persists the question + answer +
// sources to a backend snapshot so other BP users can read it later (the
// live conversation only lives in Redis with a 30-min TTL).
const BubbleFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
`

const ShareQaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const SharePublicBtn = styled(ShareQaBtn)`
  border-color: #fbcdb5;
  color: #D24724;
  &:hover {
    background: #fff5f1;
    border-color: #D24724;
    color: #b53a1d;
  }
`

const FeedbackGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-right: auto;
`

const FeedbackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid ${({ $active }) => ($active ? '#cbd5e1' : 'transparent')};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? '#f8fafc' : 'transparent')};
  color: ${({ $active }) => ($active ? '#0f172a' : '#94a3b8')};
  cursor: pointer;
  transition: color 0.15s, background 0.15s, border-color 0.15s;

  &:hover {
    background: #f8fafc;
    color: #0f172a;
  }
`

const api = APIService()

const GuruMessage = ({
  message,
  precedingUserMessage,
  userPhoto,
  onConfirmAction,
  onCancelAction,
  onOpenInsight,
  portfolioId,
  bpNameById,
}) => {
  const isUser = message.role === 'user'
  const [showTools, setShowTools] = useState(false)
  const [sharingQa, setSharingQa] = useState(false)
  const [sharingPublic, setSharingPublic] = useState(false)
  const [feedback, setFeedback] = useState(null) // 'up' | 'down' | null

  const setFeedbackAndPersist = (value) => {
    setFeedback(value)
    try {
      const key = `guru.feedback.${(message.timestamp || '').slice(0, 19)}.${(message.content || '').slice(0, 64)}`
      window.localStorage.setItem(key, value || '')
    } catch (_) {}
    if (value) {
      recordGuruFeedback({
        rating: value,
        answer: message.content,
        question: precedingUserMessage?.content || null,
        scopeType: portfolioId ? 'portfolio' : 'building_passport',
        scopeId: portfolioId || bp?.id || null,
      }).catch(() => {})
    }
  }
  // Per-citation breadcrumb state. Exactly one menu may be open at a time;
  // the active citation drives both the menu position and the share modal
  // when the user picks one of the share-related options.
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [menuCitation, setMenuCitation] = useState(null)
  const [shareModalState, setShareModalState] = useState(null) // { fileId, bpId, initialTab }
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)

  // Open the share modal against a specific citation. The modal reads BP
  // context from `getSelectedBp`; in BP Guru the selected BP already
  // matches, but in Portfolio Guru a citation may belong to a BP other
  // than the currently-selected one (or none is selected at all). We
  // pre-load the citation's BP into Redux first so the modal sees the
  // right user list and posts share calls with the right bp_id.
  const openShareModalForCitation = (citation, initialTab) => {
    const targetBpId = citation?.buildingPassportId || bp?.id
    if (!citation?.id || !targetBpId) return
    if (targetBpId !== bp?.id) {
      // This briefly shifts the global selectedBp — acceptable because
      // PortfolioDetail / Guru pages don't render BP-specific surfaces
      // that would react badly. The modal handles the loading window.
      dispatch(fetchBpRoutine({ id: targetBpId }))
    }
    setShareModalState({ fileId: citation.id, bpId: targetBpId, initialTab })
  }
  const photoCitations = message.citations?.filter((c) => c.isPhoto) || []
  const nonPhotoCitations = message.citations?.filter((c) => !c.isPhoto) || []
  const isPortfolioMode = !!portfolioId
  // The BP-side source-link relies on a single BP id; in portfolio Guru
  // citations may span many BPs, so the share-source affordance is hidden
  // there until citations carry a per-citation BP id.
  const shareContextId = isPortfolioMode ? portfolioId : bp?.id
  const hasToolInfo = !isUser && message.toolsUsed?.length > 0
  const timestamp = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  const shareSourceLink = async (citation) => {
    if (!citation?.id) return
    // Portfolio chats: each citation carries its own owning BP id (set by
    // the backend orchestrator), since the portfolio chat spans many BPs.
    // BP chats: fall back to the currently-selected BP.
    const targetBpId = citation.buildingPassportId || bp?.id
    if (!targetBpId) return
    // Dedicated route that opens the GuruInsightModal directly — no scrolling
    // through the files folder, no "No files found" empty state.
    const url = `${window.location.origin}/bp-list/${targetBpId}/source/${citation.id}`
    const ok = await copyToClipboard(url)
    if (ok) {
      toast.success('Source link copied — anyone with access to this Building Passport can open it.')
    } else {
      toast.info(`Copy this source link: ${url}`)
    }
  }

  // Creates a share row scoped to this Guru context (BP or portfolio).
  // The returned id is reused by the team-link and public-link flows.
  const createShare = async () => {
    const endpoint = isPortfolioMode
      ? `/portfolios/${portfolioId}/guru-shares`
      : `/building-passport/${bp.id}/guru-shares`
    const res = await api.post(endpoint, {
      question: precedingUserMessage.content,
      answer: message.content,
      citations: message.citations || [],
      webSources: message.webSources || [],
      toolsUsed: message.toolsUsed || [],
    })
    const shareId = res?.data?.data?.id
    if (!shareId) throw new Error('No share id in response')
    return shareId
  }

  const buildInternalShareUrl = (shareId) => {
    const origin = window.location.origin
    return isPortfolioMode
      ? `${origin}/portfolios/${portfolioId}/guru-shares/${shareId}`
      : `${origin}/bp-list/${bp.id}/guru-shares/${shareId}`
  }

  const memberAudienceLabel = isPortfolioMode
    ? 'Link copied — anyone this portfolio is shared with can open it.'
    : 'Link copied — any member of this Building Passport can open it.'

  const shareQa = async () => {
    if (sharingQa || sharingPublic || isUser || !shareContextId) return
    if (!precedingUserMessage?.content || !message.content) {
      toast.error('Cannot share this answer — the original question is no longer available.')
      return
    }
    setSharingQa(true)
    try {
      const shareId = await createShare()
      const url = buildInternalShareUrl(shareId)
      const ok = await copyToClipboard(url)
      if (ok) {
        toast.success(memberAudienceLabel)
      } else {
        toast.info(`Share created. Copy this link: ${url}`)
      }
    } catch (e) {
      toast.error('Could not create the share link. Please try again.')
    } finally {
      setSharingQa(false)
    }
  }

  const sharePublicly = async () => {
    if (sharingQa || sharingPublic || isUser || !shareContextId) return
    if (!precedingUserMessage?.content || !message.content) {
      toast.error('Cannot share this answer — the original question is no longer available.')
      return
    }
    setSharingPublic(true)
    try {
      const shareId = await createShare()
      const pub = await api.post(`/guru-shares/${shareId}/publish`, {})
      const publicUrl = pub?.data?.data?.publicUrl
      if (!publicUrl) throw new Error('No public URL returned')
      const ok = await copyToClipboard(publicUrl)
      if (ok) {
        toast.success('External link copied — anyone with the link can view this Q&A, no login needed.')
      } else {
        toast.info(`External link: ${publicUrl}`)
      }
    } catch (e) {
      toast.error('Could not create the public link. Please try again.')
    } finally {
      setSharingPublic(false)
    }
  }

  const canShareQa = !isUser
    && !message.isWelcome
    && !message.isError
    && shareContextId
    && precedingUserMessage?.content
    && message.content

  const memberAudienceTooltip = isPortfolioMode
    ? 'Copy a link anyone this portfolio is shared with can open'
    : 'Copy a link other members of this Building Passport can open'

  // Both surfaces use the same primary label "Copy Link" — the audience is
  // captured in the tooltip and the success toast.
  const memberAudienceBtnLabel = 'Copy Link'

  return (
    <>
      <MessageContainer $isUser={isUser}>
        {!isUser && (
          <AvatarWrapper>
            <HeaderAvatar src={LogoShort} alt="Guru" />
          </AvatarWrapper>
        )}
        <MessageContent $isUser={isUser} $isError={message.isError}>
          <MessageHeader>
            <MessageSender $isUser={isUser}>
              {isUser ? 'You' : 'Guru'}
            </MessageSender>
            {hasToolInfo && (
              <Tooltip title="Show what Guru checked to answer this" placement="top">
                <ToolInfoIcon size="small" onClick={() => setShowTools(!showTools)}>
                  <InfoOutlinedIcon />
                </ToolInfoIcon>
              </Tooltip>
            )}
          </MessageHeader>
          {showTools && hasToolInfo && (
            <ToolList>
              {message.toolsUsed.map((tool, i) => (
                <ToolChip key={i}>{TOOL_LABELS[tool] || tool}</ToolChip>
              ))}
            </ToolList>
          )}
          <MarkdownRenderer content={message.content} />
          {photoCitations.length > 0 && (
            <GuruPhotoSources
              citations={photoCitations}
              fallbackBpId={bp?.id}
            />
          )}
          {nonPhotoCitations.length > 0 && (
            <CitationsContainer>
              <CitationsLabel>Sources</CitationsLabel>
              <CitationsList>
                {nonPhotoCitations.map((citation, i) => (
                  <CitationRow key={citation.id || i}>
                    <CitationSlot>
                      <ReportSourceCitation
                        source={{
                          name: citation.name,
                          fileUrl: citation.id ? true : null,
                          relevanceScore: citation.score,
                          buildingPassportId: citation.buildingPassportId,
                          isEstateDocument: citation.isEstateDocument,
                        }}
                        bpNameById={bpNameById}
                        onClick={() => {
                          // Portfolio chat citations carry their owning BP id;
                          // BP chats reuse the selected BP. Either way, pass
                          // it up so the insight modal opens against the
                          // right BP.
                          const targetBpId = citation.buildingPassportId || bp?.id
                          if (!citation.id || !targetBpId) return
                          if (onOpenInsight) {
                            onOpenInsight(citation.id, targetBpId)
                          }
                        }}
                      />
                    </CitationSlot>
                    {citation.id && (citation.buildingPassportId || bp?.id) && (
                      <Tooltip title="More actions for this source" placement="top">
                        <ShareSourceBtn
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            setMenuAnchor(e.currentTarget)
                            setMenuCitation(citation)
                          }}
                          aria-label="Source options"
                        >
                          <MoreVertIcon style={{ fontSize: 18 }} />
                        </ShareSourceBtn>
                      </Tooltip>
                    )}
                  </CitationRow>
                ))}
              </CitationsList>
            </CitationsContainer>
          )}
          {message.webSources?.length > 0 && (
            <WebSourcesContainer>
              <WebSourcesLabel>Web Sources Consulted</WebSourcesLabel>
              <WebSourcesList>
                {message.webSources.map((domain, i) => (
                  <WebSourceChip
                    key={i}
                    onClick={() => window.open(`https://${domain}`, '_blank', 'noopener,noreferrer')}
                  >
                    {domain}
                  </WebSourceChip>
                ))}
              </WebSourcesList>
              <WebSourcesDisclaimer>
                Information from external websites — verify with official sources for compliance decisions.
              </WebSourcesDisclaimer>
            </WebSourcesContainer>
          )}
          {!isUser && !message.isWelcome && !message.isError && (
            <AiDisclaimer>
              AI-generated response — may not fully reflect your documents. Always verify critical information.
            </AiDisclaimer>
          )}
          {canShareQa && (
            <BubbleFooter>
              <FeedbackGroup>
                <Tooltip title="Helpful">
                  <FeedbackBtn
                    type="button"
                    $active={feedback === 'up'}
                    aria-label="Mark response as helpful"
                    onClick={() => setFeedbackAndPersist(feedback === 'up' ? null : 'up')}
                  >
                    {feedback === 'up' ? <ThumbUpAltIcon style={{ fontSize: 14 }} /> : <ThumbUpAltOutlinedIcon style={{ fontSize: 14 }} />}
                  </FeedbackBtn>
                </Tooltip>
                <Tooltip title="Not helpful">
                  <FeedbackBtn
                    type="button"
                    $active={feedback === 'down'}
                    aria-label="Mark response as not helpful"
                    onClick={() => setFeedbackAndPersist(feedback === 'down' ? null : 'down')}
                  >
                    {feedback === 'down' ? <ThumbDownAltIcon style={{ fontSize: 14 }} /> : <ThumbDownAltOutlinedIcon style={{ fontSize: 14 }} />}
                  </FeedbackBtn>
                </Tooltip>
              </FeedbackGroup>
              <Tooltip title={memberAudienceTooltip} placement="top">
                <ShareQaBtn type="button" onClick={shareQa} disabled={sharingQa || sharingPublic}>
                  <LinkIcon style={{ fontSize: 14 }} />
                  {sharingQa ? 'Copying…' : memberAudienceBtnLabel}
                </ShareQaBtn>
              </Tooltip>
              <Tooltip title="Create an external link — anyone with the link can view this Q&A, no login required" placement="top">
                <SharePublicBtn type="button" onClick={sharePublicly} disabled={sharingQa || sharingPublic}>
                  <IosShareIcon style={{ fontSize: 14 }} />
                  {sharingPublic ? 'Sharing…' : 'Share Externally'}
                </SharePublicBtn>
              </Tooltip>
            </BubbleFooter>
          )}
          {timestamp && <MessageTime>{timestamp}</MessageTime>}
        </MessageContent>
        {isUser && (
          <AvatarWrapper>
            {userPhoto ? (
              <UserAvatar src={userPhoto} alt="You" />
            ) : (
              <InitialsAvatar />
            )}
          </AvatarWrapper>
        )}
      </MessageContainer>
      {message.actions?.map((action, i) => (
        <GuruActionCard
          key={i}
          action={action}
          onConfirm={onConfirmAction}
          onCancel={onCancelAction}
        />
      ))}

      {/* Citation actions menu — three options that mirror the Documents
          row breadcrumb: manage internal permissions, copy the source-link
          to the in-app insight modal, copy externally (invite-by-email
          flow). All three operate on `menuCitation` which is set by the
          MoreVert button in each citation row. */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor && !!menuCitation}
        onClose={() => { setMenuAnchor(null); setMenuCitation(null) }}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 220, mt: 0.5 } }}
      >
        <MenuItem
          onClick={() => {
            const c = menuCitation
            setMenuAnchor(null); setMenuCitation(null)
            openShareModalForCitation(c, 0)
          }}
        >
          <ListItemIcon><GroupOutlinedIcon fontSize="small" /></ListItemIcon>
          Manage permissions
        </MenuItem>
        <MenuItem
          onClick={() => {
            const c = menuCitation
            setMenuAnchor(null); setMenuCitation(null)
            if (c) shareSourceLink(c)
          }}
        >
          <ListItemIcon><ContentCopyOutlinedIcon fontSize="small" /></ListItemIcon>
          Copy link
        </MenuItem>
        <MenuItem
          onClick={() => {
            const c = menuCitation
            setMenuAnchor(null); setMenuCitation(null)
            openShareModalForCitation(c, 1)
          }}
        >
          <ListItemIcon><OpenInNewOutlinedIcon fontSize="small" /></ListItemIcon>
          Copy externally
        </MenuItem>
      </Menu>

      {/* Defer rendering the modal until selectedBp matches the citation's
          BP. Otherwise the user list & share endpoint would briefly use
          the wrong context. The fetchBpRoutine dispatched from
          openShareModalForCitation populates getSelectedBp asynchronously. */}
      {shareModalState && bp?.id === shareModalState.bpId && (
        <BulkShareFilesModal
          open
          onClose={() => setShareModalState(null)}
          selectedFiles={[shareModalState.fileId]}
          initialTab={shareModalState.initialTab}
        />
      )}
    </>
  )
}

export default GuruMessage
